import type { WalletGroupedAccounts } from '@polaris/core/models'
import { addAccountsToPortfolio } from '@polaris/core/models'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { ampli } from '@/src/codegen/ampli'
import {
  showNewWalletAccountAddedToast,
  showNewWalletDetectedToast,
} from '@/src/components/ui/toast'
import { getQueryClient } from '@/src/configs/tanstack-query'
import { userAccountsQuery } from '@/src/queries/keys/user'
import { setAccounts as setAccountsService } from '@/src/services/user'
import { changingAccountWalletInfoAtom } from '../select-wallets'
import { userAtom } from '../user/atoms'
import { walletOptionsAtom } from '../wallets/atoms'
import { isValidAccountsState, useAccountsActions } from './actions'
import {
  accountsAtom,
  isConnectingWalletAtom,
  isLocalStateModeAtom,
  localAccountsAtom,
  userAccountsAtom,
} from './atoms'
import {
  hasAddedWalletAtom,
  newlyAddedWalletNameAtom,
  pendingAccountsAtom,
  walletAccountsAtom,
} from './wallets'

/** Wallet names currently waiting for a timeout that ensures the wallet connectors have a bit of time to update state once an account is switched in a wallet. */
const awaitWalletStateSettleAtom = atom<Set<string>>(new Set<string>())
/** Wallets that had a pending account detected that are ready to have the account be added where the timeout above has completed. */
const readyPendingWalletsAtom = atom<Set<string>>(new Set<string>())
/** Once the above wallet-wide timeout has completed, we can mark the updated grouping key as pending. */
const readyPendingGroupingKeysAtom = atom<Set<string>>(new Set<string>())

/** Set of grouping keys that have been processed to avoid re-processing the same wallet group or immediately re-adding a wallet as ready while it's still being processed. */
export const processedGroupingKeysAtom = atom<Set<string>>(new Set<string>())

/** Set of grouping keys for toasts that have been dismissed to avoid showing them again. */
export const dismissedToastsAtom = atom<Set<string>>(new Set<string>())

/** Wallet connection queue for wallets they just connected where the accounts should be immediately added. */
export const walletConnectionQueueAtom = atom<Set<string>>(new Set<string>())

/** Set of grouping keys for which auto-connect has been requested. */
const autoConnectRequestedGroupingKeysAtom = atom<Set<string>>(
  new Set<string>(),
)

/** Atom to store the grouping key of the pending account to display in the dialog. */
export const pendingAccountDialogAtom = atom<string | null>(null)

/**
 * This effect monitors pending wallet accounts and proceeds with ensuring
 * remaining ecosystem accounts are connected and then automatically adding
 * them to the user's account set.
 */
export const showPendingAccountToastAtom = atomEffect((get, set) => {
  const accounts = get(accountsAtom)
  const { groupedAccounts, accountsWithWallets } = get(pendingAccountsAtom)
  const { aggregatedWallets, allReady } = get(walletOptionsAtom)
  const readyPendingGroupingKeys = get(readyPendingGroupingKeysAtom)
  const isConnectingWallet = get(isConnectingWalletAtom)
  const walletConnectionQueue = get(walletConnectionQueueAtom)
  const processedGroupingKeys = get(processedGroupingKeysAtom)
  const dismissedToasts = get(dismissedToastsAtom)
  const autoConnectRequestedGroupingKeys = get(
    autoConnectRequestedGroupingKeysAtom,
  )
  const user = get(userAtom)
  const isLocalState = get(isLocalStateModeAtom)
  const changingAccountWalletInfo = get(changingAccountWalletInfoAtom)
  const pendingAccountDialog = get(pendingAccountDialogAtom)

  if (!allReady) return

  // Don't add accounts while any wallet is connecting
  if (isConnectingWallet) return
  if (
    window.location.pathname.startsWith('/login') ||
    window.location.pathname.startsWith('/auth')
  )
    return

  // Users can deal with multiple pending accounts at a time, so the processing is iterative across current pending accounts
  for (const currentPendingAccount of groupedAccounts) {
    const toastId = `${currentPendingAccount.provider_info.name}-${currentPendingAccount.grouping_key}`

    if (!readyPendingGroupingKeys.has(currentPendingAccount.grouping_key))
      continue

    const currentPendingWallet = aggregatedWallets.find(
      (wallet) =>
        wallet.displayName === currentPendingAccount.provider_info.name,
    )
    // This should never happen as pending accounts are derived from the wallet options
    if (!currentPendingWallet) continue

    // Check if there's already a toast for this wallet and dismiss it
    const existingToasts = toast.getToasts()
    const existingWalletToasts = existingToasts.filter(
      (toastItem) =>
        toastItem.id &&
        typeof toastItem.id === 'string' &&
        toastItem.id.startsWith(
          `${currentPendingAccount.provider_info.name}-`,
        ) &&
        toastItem.id !== toastId,
    )

    // Dismiss any existing toasts for this wallet
    existingWalletToasts.forEach((existingToast) => {
      toast.dismiss(existingToast.id)
    })

    const needsToConnectWallet =
      Object.keys(currentPendingWallet.disconnectors).length !==
      Object.keys(currentPendingWallet.connectors).length

    // If the wallet has some missing connections, connect to them
    // BUT: Never auto-connect for first-time connections (in queue)
    const isFirstTimeConnection = walletConnectionQueue.has(
      currentPendingAccount.provider_info.name,
    )

    if (
      needsToConnectWallet &&
      currentPendingWallet.connect &&
      !autoConnectRequestedGroupingKeys.has(
        currentPendingAccount.grouping_key,
      ) &&
      !isFirstTimeConnection
    ) {
      set(isConnectingWalletAtom, true)

      // Add the grouping key to the auto connect requested set
      set(autoConnectRequestedGroupingKeysAtom, (prev) => {
        const newSet = new Set(prev)
        newSet.add(currentPendingAccount.grouping_key)
        return newSet
      })

      currentPendingWallet.connect().finally(() => {
        setTimeout(() => {
          set(isConnectingWalletAtom, false)
        }, 1_000) // 1000ms to let the connection propagate
      })
    } else if (!needsToConnectWallet) {
      // Add the grouping key to the auto connect requested set since it's already connected
      // This allows other wallets to hijack ecosystem connections and prevents this wallet from auto connecting
      set(autoConnectRequestedGroupingKeysAtom, (prev) => {
        const newSet = new Set(prev)
        newSet.add(currentPendingAccount.grouping_key)
        return newSet
      })
    }

    // Continue if already processed
    if (processedGroupingKeys.has(currentPendingAccount.grouping_key)) continue

    if (
      changingAccountWalletInfo?.walletOption.displayName ===
      currentPendingAccount.provider_info.name
    ) {
      continue
    }

    const accountsToAdd = accountsWithWallets.filter(
      (account) => account.grouping_key === currentPendingAccount.grouping_key,
    )

    const updatedAccountsState = addAccountsToPortfolio(accountsToAdd, accounts)

    // Validate updated accounts for max limit and Keplr invalid grouping state
    if (!isValidAccountsState(updatedAccountsState, accountsWithWallets, ampli))
      continue

    // Pop wallet connection queue and auto add the account instead of showing toast
    if (walletConnectionQueue.has(currentPendingAccount.provider_info.name)) {
      set(processedGroupingKeysAtom, (prev) => {
        const newSet = new Set(prev)
        newSet.add(currentPendingAccount.grouping_key)
        return newSet
      })
      set(walletConnectionQueueAtom, (prev) => {
        const newSet = new Set(prev)
        newSet.delete(currentPendingAccount.provider_info.name)
        return newSet
      })

      // Add the account

      // Dismiss the current wallet toast
      toast.dismiss(toastId)

      if (isLocalState) {
        set(localAccountsAtom, updatedAccountsState)
      } else {
        setAccountsService(user, updatedAccountsState).finally(() => {
          getQueryClient().refetchQueries(userAccountsQuery())
        })
      }

      // Trigger success screen in dialog
      set(hasAddedWalletAtom, true)
      set(newlyAddedWalletNameAtom, currentPendingAccount.provider_info.name)

      continue
    }

    // Don't show toasts for detected accounts if they haven't added any yet via connect wallet dialog via addAccount call above.
    // This prevents zombie connections from triggering detection logic if they log out for example.
    // Or remove all accounts manually.
    if (accounts.length === 0) continue

    // If already a toast, do nothing
    if (existingToasts.some((toast) => toast.id === toastId)) continue

    if (pendingAccountDialog) continue

    // Don't show toast if this grouping key was already dismissed
    if (dismissedToasts.has(currentPendingAccount.grouping_key)) continue

    // CRITICAL: Never show toast for wallets in connection queue
    // These are first-time connections, toasts are ONLY for account switching in existing wallets
    if (walletConnectionQueue.has(currentPendingAccount.provider_info.name)) {
      continue
    }

    showNewWalletDetectedToast({
      toastId,
      walletImg: currentPendingAccount.provider_info.img,
      walletName: currentPendingAccount.provider_info.name,
      accountName: currentPendingAccount.name,
      onAddAccount: () => {
        toast.dismiss(toastId)
        set(pendingAccountDialogAtom, currentPendingAccount.grouping_key)
        set(dismissedToastsAtom, (prev) => {
          const newSet = new Set(prev)
          newSet.add(currentPendingAccount.grouping_key)
          return newSet
        })
      },
      onDismiss: () => {
        toast.dismiss(toastId)
        set(dismissedToastsAtom, (prev) => {
          const newSet = new Set(prev)
          newSet.add(currentPendingAccount.grouping_key)
          return newSet
        })
      },
    })
  }
})

/**
 * Listens to new pending accounts that appear and sets a small timeout per wallet to allow each connector
 * to update their state independently and prevent premature account groupings from being created from current account connections.
 */
export const walletStateSettleEffectAtom = atomEffect((get, set) => {
  const accounts = get(accountsAtom)
  const { groupedAccounts } = get(pendingAccountsAtom)
  const awaitWalletStateSettle = get(awaitWalletStateSettleAtom)
  const readyPendingWallets = get(readyPendingWalletsAtom)
  const readyPendingGroupingKeys = get(readyPendingGroupingKeysAtom)
  const processedGroupingKeys = get(processedGroupingKeysAtom)
  const userAccountsQuery = get(userAccountsAtom)
  const isConnectingWallet = get(isConnectingWalletAtom)

  if (isConnectingWallet) return

  // Don't process wallet state settling if user accounts are still fetching
  if (userAccountsQuery.isFetching) return

  for (const currentPendingAccount of groupedAccounts) {
    const walletName = currentPendingAccount.provider_info.name

    // If this wallet is not in the awaiting map, add it and start timer
    if (
      !awaitWalletStateSettle.has(walletName) &&
      !readyPendingWallets.has(walletName) &&
      !processedGroupingKeys.has(currentPendingAccount.grouping_key) &&
      !readyPendingGroupingKeys.has(currentPendingAccount.grouping_key) &&
      !accounts.some(
        (account) =>
          account.grouping_key === currentPendingAccount.grouping_key,
      )
    ) {
      // Add to awaiting set
      set(awaitWalletStateSettleAtom, (prev: Set<string>) => {
        const newSet = new Set(prev)
        newSet.add(walletName)
        return newSet
      })

      // Set 1000ms timeout to move wallet to ready state
      setTimeout(() => {
        // Remove from awaiting set
        set(awaitWalletStateSettleAtom, (prev: Set<string>) => {
          const newSet = new Set(prev)
          newSet.delete(walletName)
          return newSet
        })

        // Add to ready set
        set(readyPendingWalletsAtom, (prev: Set<string>) => {
          const newSet = new Set(prev)
          newSet.add(walletName)
          return newSet
        })
      }, 1_000) // 1000ms
    }
  }
})

/**
 * Drain the wallet queue if an account is already added to the user's account set
 * to prevent downstream accounts from being auto added.
 */
export const walletQueueDrainEffectAtom = atomEffect((get, set) => {
  const { groupedAccounts } = get(walletAccountsAtom)
  const awaitingStateSettleWallets = get(awaitWalletStateSettleAtom)

  for (const account of groupedAccounts) {
    if (awaitingStateSettleWallets.has(account.provider_info.name)) {
      // Don't process unless ready (wallets may be updating state), this effect will rerun
      continue
    }

    if (account.walletConnectionStatus !== 'none') {
      set(walletConnectionQueueAtom, (prev) => {
        const newSet = new Set(prev)
        newSet.delete(account.provider_info.name)
        return newSet
      })
    }

    // Clean up processed grouping keys to prepare for potential down stream detection
    set(processedGroupingKeysAtom, (prev) => {
      const newSet = new Set(prev)
      newSet.delete(account.grouping_key)
      return newSet
    })

    set(autoConnectRequestedGroupingKeysAtom, (prev) => {
      const newSet = new Set(prev)
      newSet.delete(account.grouping_key)
      return newSet
    })
  }
})

/**
 * Convert per-wallet ready flags into specific grouping keys that are ready to be added (either automatically or via user action)
 */
export const readyPendingGroupingKeyEffectAtom = atomEffect((get, set) => {
  const { groupedAccounts } = get(pendingAccountsAtom)
  const readyPendingWallets = get(readyPendingWalletsAtom)
  const readyPendingGroupingKeys = get(readyPendingGroupingKeysAtom)
  const { allReady } = get(walletOptionsAtom)

  if (!allReady) return

  for (const currentPendingAccount of groupedAccounts) {
    const walletName = currentPendingAccount.provider_info.name
    const groupingKey = currentPendingAccount.grouping_key

    if (readyPendingWallets.has(walletName)) {
      set(readyPendingWalletsAtom, (prev) => {
        const newSet = new Set(prev)
        newSet.delete(walletName)
        return newSet
      })

      set(readyPendingGroupingKeysAtom, (prev) => {
        const newSet = new Set(prev)
        newSet.add(groupingKey)
        return newSet
      })
    }
  }

  // Clean up any ready pending grouping keys that are no longer in the pending accounts
  for (const groupingKey of readyPendingGroupingKeys) {
    if (
      !groupedAccounts.some((account) => account.grouping_key === groupingKey)
    ) {
      set(readyPendingGroupingKeysAtom, (prev) => {
        const newSet = new Set(prev)
        newSet.delete(groupingKey)
        return newSet
      })
    }
  }
})

/**
 * Hook to open/close the pending account dialog and dismiss relevant toasts.
 * Returns a setter callback that accepts a grouping key or null to close.
 */
export function usePendingAccountDialog() {
  const setDialog = useSetAtom(pendingAccountDialogAtom)
  const setDismissedToasts = useSetAtom(dismissedToastsAtom)
  const { groupedAccounts } = useAtomValue(pendingAccountsAtom)

  const setPendingAccountDialog = useCallback(
    (groupingKey: string | null) => {
      const existingToasts = toast.getToasts()

      if (groupingKey) {
        const pendingAccount = groupedAccounts.find(
          (acc) => acc.grouping_key === groupingKey,
        )

        if (pendingAccount) {
          const walletPrefix = `${pendingAccount.provider_info.name}-`
          existingToasts.forEach((t) => {
            if (
              t.id &&
              typeof t.id === 'string' &&
              (t.id === `${walletPrefix}${groupingKey}` ||
                t.id.startsWith(walletPrefix))
            ) {
              toast.dismiss(t.id)
            }
          })
        } else {
          // Fallback: dismiss all active toasts if we can't resolve the wallet
          existingToasts.forEach((t) => {
            if (t.id !== undefined) toast.dismiss(t.id)
          })
        }

        setDialog(groupingKey)
        setDismissedToasts((prev) => {
          const next = new Set(prev)
          next.add(groupingKey)
          return next
        })
      } else {
        // Closing dialog: dismiss any lingering toasts
        existingToasts.forEach((t) => {
          if (t.id !== undefined) toast.dismiss(t.id)
        })
        setDialog(null)
      }
    },
    [groupedAccounts, setDialog, setDismissedToasts],
  )

  return setPendingAccountDialog
}

/**
 * Hook exposing shared actions for handling pending accounts.
 * Consolidates logic used by pending account UI components.
 */
export function usePendingAccountActions() {
  const { addAccounts, addWalletGroupedAccounts } = useAccountsActions()
  const setProcessedGroupingKeys = useSetAtom(processedGroupingKeysAtom)
  const setReadyPendingGroupingKeys = useSetAtom(readyPendingGroupingKeysAtom)
  const setAwaitWalletStateSettle = useSetAtom(awaitWalletStateSettleAtom)
  const setDismissedToasts = useSetAtom(dismissedToastsAtom)

  const addPendingAccount = useCallback(
    async (account: WalletGroupedAccounts) => {
      // Dismiss any existing toast for this wallet/grouping key
      toast.dismiss(`${account.provider_info.name}-${account.grouping_key}`)

      // Add the account group to the portfolio
      await addWalletGroupedAccounts(account)

      // Mark grouping key processed and clear ready state
      setProcessedGroupingKeys((prev) => {
        const next = new Set(prev)
        next.add(account.grouping_key)
        return next
      })
      setReadyPendingGroupingKeys((prev) => {
        const next = new Set(prev)
        next.delete(account.grouping_key)
        return next
      })

      const toastId = `${account.provider_info.name}-${account.grouping_key}`

      showNewWalletAccountAddedToast({
        toastId,
        walletImg: account.provider_info.img,
        walletName: account.provider_info.name,
        accountName: account.name,
        onDismiss: () => {
          toast.dismiss(toastId)
          setDismissedToasts((prev) => {
            const newSet = new Set(prev)
            newSet.add(account.grouping_key)
            return newSet
          })
        },
      })
    },
    [
      addWalletGroupedAccounts,
      setDismissedToasts,
      setProcessedGroupingKeys,
      setReadyPendingGroupingKeys,
    ],
  )

  const cleanupRemovedAccount = useCallback(
    ({
      providerName,
      groupingKey,
    }: {
      providerName: string
      groupingKey: string
    }) => {
      // Hold this wallet name to prevent premature ready while removal/disconnect propagates
      setAwaitWalletStateSettle((prev) => {
        const next = new Set(prev)
        next.add(providerName)
        return next
      })
      // Add a slight delay to compensate for slow wallet libs updating state
      setTimeout(() => {
        setAwaitWalletStateSettle((prev) => {
          const next = new Set(prev)
          next.delete(providerName)
          return next
        })
        setProcessedGroupingKeys((prev) => {
          const newSet = new Set(prev)
          newSet.delete(groupingKey)
          return newSet
        })
      }, 1000)

      setDismissedToasts((prev) => {
        const newSet = new Set(prev)
        newSet.delete(groupingKey)
        return newSet
      })
    },
    [setAwaitWalletStateSettle, setProcessedGroupingKeys, setDismissedToasts],
  )

  return {
    addPendingAccount,
    /** Prepare account to potentially become pending again later. */
    cleanupRemovedAccount,
    isAddingAccount: addAccounts.isPending,
  }
}
